import { useState } from "react";
import { MapPin, Phone, Mail, Clock, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const contactInfo = [
  {
    icon: MapPin,
    title: "Our Office",
    details: ["Sydney, NSW", "Australia"]
  },
  {
    icon: Phone,
    title: "Phone",
    details: ["+61 2 9538 7640"]
  },
  {
    icon: Mail,
    title: "Email",
    details: ["hello@cloudjet.com.au"]
  },
  {
    icon: Clock,
    title: "Business Hours",
    details: ["Mon - Fri: 9AM - 6PM", "Sat: 10AM - 4PM"]
  }
];

const travelInterests = [
  "Beach & Island Getaway",
  "Adventure Travel",
  "Luxury Vacation",
  "Family Holiday",
  "Honeymoon",
  "Group Travel",
  "Corporate Travel",
  "Other"
];

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    travel_interest: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Thank you! We'll be in touch soon.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        travel_interest: "",
        message: ""
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block bg-sky-100 text-sky-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            Get In Touch
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-sky-900 mb-4">
            Start Planning Your Dream Trip
          </h2>
          <p className="text-sky-600 max-w-2xl mx-auto text-lg">
            Ready to explore? Fill out the form below and our travel experts will help you create the perfect itinerary.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12">
          {/* Contact Info */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-sky-600 text-white rounded-2xl p-8">
              <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
              <p className="text-sky-100 mb-8">
                Our friendly team is here to help you plan your next adventure. 
                Reach out through any of these channels.
              </p>

              <div className="space-y-6">
                {contactInfo.map((item, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="w-12 h-12 bg-sky-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <item.icon size={24} />
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">{item.title}</h4>
                      {item.details.map((detail, dIndex) => (
                        <p key={dIndex} className="text-sky-100 text-sm">{detail}</p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Map placeholder */}
            <div className="rounded-2xl overflow-hidden shadow-lg border border-sky-100">
              <iframe
                title="CloudJet Office Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d212270.6461975972!2d150.85191717968754!3d-33.84737219999999!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6b129838f39a743f%3A0x3017d681632a850!2sSydney%20NSW%2C%20Australia!5e0!3m2!1sen!2sus!4v1699990000000!5m2!1sen!2sus"
                width="100%"
                height="200"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map"
              ></iframe>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-3">
            <form onSubmit={handleSubmit} className="bg-sky-50 rounded-2xl p-8 border border-sky-100" data-testid="contact-form">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Name */}
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sky-900 font-medium">
                    Full Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="John Smith"
                    value={formData.name}
                    onChange={handleChange}
                    className="bg-white border-sky-200 focus:border-sky-500"
                    required
                    data-testid="contact-name-input"
                  />
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sky-900 font-medium">
                    Email Address <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="john@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    className="bg-white border-sky-200 focus:border-sky-500"
                    required
                    data-testid="contact-email-input"
                  />
                </div>

                {/* Phone */}
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-sky-900 font-medium">
                    Phone Number
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="+61 4XX XXX XXX"
                    value={formData.phone}
                    onChange={handleChange}
                    className="bg-white border-sky-200 focus:border-sky-500"
                    data-testid="contact-phone-input"
                  />
                </div>

                {/* Travel Interest */}
                <div className="space-y-2">
                  <Label htmlFor="travel_interest" className="text-sky-900 font-medium">
                    Travel Interest
                  </Label>
                  <select
                    id="travel_interest"
                    name="travel_interest"
                    value={formData.travel_interest}
                    onChange={handleChange}
                    className="w-full h-10 px-3 rounded-md border border-sky-200 bg-white text-sky-900 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20"
                    data-testid="contact-interest-select"
                  >
                    <option value="">Select your interest</option>
                    {travelInterests.map((interest, index) => (
                      <option key={index} value={interest}>{interest}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Message */}
              <div className="space-y-2 mt-6">
                <Label htmlFor="message" className="text-sky-900 font-medium">
                  Your Message <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  placeholder="Tell us about your dream vacation..."
                  value={formData.message}
                  onChange={handleChange}
                  className="bg-white border-sky-200 focus:border-sky-500 min-h-[150px]"
                  required
                  data-testid="contact-message-input"
                />
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full mt-6 bg-sky-600 hover:bg-sky-700 text-white py-6 text-lg"
                data-testid="contact-submit-btn"
              >
                {isSubmitting ? (
                  "Sending..."
                ) : (
                  <>
                    <Send className="mr-2" size={20} />
                    Send Message
                  </>
                )}
              </Button>

              <p className="text-sky-600 text-sm text-center mt-4">
                We typically respond within 24 hours during business days.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
